import React from "react";
import { observer } from "mobx-react";
import { useHistory } from "react-router-dom";
import copilotStore from "Stores/copilotStore";
import renderMarkdown from "../markdown";

const fmt = n => Number(n || 0).toLocaleString("en-US");

// An agent about to be created from chat: its steps as icon chips, when it runs and who
// hears about it, then exactly what one run costs against the balance.
const AgentPreview = ({ preview, resolved }) => {
  const history = useHistory();
  const c = preview.credits || {};
  const short = c.sufficient === false;
  return (
    <div className="copilotAgentPreview">
      <div className="copilotAgentPreview__steps">
        {(preview.steps || []).map((step, i) => (
          // The arrow travels with the step after it, so a wrapped row never ends on a dangling arrow.
          <span className="copilotAgentPreview__stepWrap" key={step.label}>
            {i > 0 && <i className="material-icons-outlined copilotAgentPreview__arrow">chevron_right</i>}
            <span className="copilotAgentPreview__step">
              <i className="material-icons-outlined">{step.icon}</i>
              {step.label}
            </span>
          </span>
        ))}
      </div>
      {(preview.notes || []).length > 0 && (
        <ul className="copilotAgentPreview__notes">
          {preview.notes.map(n => (
            <li key={n.text}>
              <i className="material-icons-outlined">{n.icon}</i>
              <span>{n.text}</span>
            </li>
          ))}
        </ul>
      )}
      {(c.lines || []).length > 0 && (
        <div className={`copilotAgentPreview__credits${short ? " is-short" : ""}`}>
          {c.lines.map(l => (
            <div className="copilotAgentPreview__creditRow" key={l.label}>
              <span>{l.label}</span>
              <span>{fmt(l.credits)}</span>
            </div>
          ))}
          <div className="copilotAgentPreview__creditRow is-total">
            <span>This run uses</span>
            <span>{fmt(c.needed)} credits</span>
          </div>
          {c.remaining !== null && c.remaining !== undefined && (
            <div className="copilotAgentPreview__balance">
              You have {fmt(c.remaining)}
              {short ? (
                <> · {fmt(c.needed - c.remaining)} short. The agent is saved, and starts once you add credits.</>
              ) : (
                <> · {fmt(c.after)} left after this run</>
              )}
            </div>
          )}
          {short && !resolved && (
            <button type="button" className="copilotConfirm__btn primary copilotAgentPreview__add"
              onClick={() => history.push(preview.addCreditsHref || "/settings/billing")}>
              <i className="material-icons-outlined">credit_card</i>Add credits
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const ConfirmationBlock = observer(({ block }) => {
  const resolved = Boolean(block.resolution);
  const destructive = Boolean(block.destructive);
  // Costs credits, writes outside Salesgear or changes a team setting: amber, labelled.
  const needsOk = Boolean(block.needsOk);

  return (
    <div
      className={`copilotConfirm ${block.title ? "copilotConfirm--card" : ""} ${
        resolved ? "resolved" : ""
      } ${destructive ? "destructive" : ""} ${needsOk ? "copilotConfirm--needsOk" : ""}`}
    >
      {block.title ? (
        // Same card the CTA plan uses (title / body / facts), so a confirm
        // looks like every other decision card in the thread.
        <div className="copilotCtaCard copilotConfirm__card">
          {needsOk && !resolved && (
            <div className="copilotConfirm__eyebrow">
              <i className="material-icons-outlined">pan_tool</i>Needs your OK
            </div>
          )}
          <div className="copilotCtaCard__title">{block.title}</div>
          {block.body && <div className="copilotCtaCard__body">{block.body}</div>}
          {block.agentPreview && <AgentPreview preview={block.agentPreview} resolved={resolved} />}
          {Array.isArray(block.facts) && block.facts.length > 0 && (
            <dl className="copilotCtaCard__facts">
              {block.facts.map(f => (
                <React.Fragment key={f.label}>
                  <dt className="copilotCtaCard__factLabel">{f.label}</dt>
                  <dd>{f.value}</dd>
                </React.Fragment>
              ))}
            </dl>
          )}
          {/* Who this is about: the same peek rows the table card shows, so a
              "save the top 5" confirm names the people, not just a count. */}
          {block.preview && Array.isArray(block.preview.rows) && block.preview.rows.length > 0 && (
            <div className="copilotTableCard__peek copilotConfirm__preview">
              <div className="copilotTableCard__peekRows">
                <div className="copilotTableCard__peekRow is-head">
                  {block.preview.columns.map(c => (
                    <span className="copilotTableCard__peekCell" key={c.key}>
                      {c.label || c.key}
                    </span>
                  ))}
                </div>
                {block.preview.rows.map((row, i) => (
                  <div className="copilotTableCard__peekRow" key={row.id || i}>
                    {block.preview.columns.map((c, j) => (
                      <span
                        className={`copilotTableCard__peekCell ${j === 0 ? "is-primary" : ""}`}
                        key={c.key}
                        title={row[c.key] || ""}
                      >
                        {row[c.key] || ""}
                      </span>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="copilotConfirm__prompt">
          <i className="material-icons-outlined">
            {destructive ? "error_outline" : "check_circle_outline"}
          </i>
          {/* The prompt carries **bold** — it read as literal asterisks before. */}
          <div className="copilotConfirm__promptBody">
            {renderMarkdown(block.prompt)}
          </div>
        </div>
      )}
      {!resolved ? (
        <div className="copilotConfirm__actions">
          <button
            type="button"
            className={`copilotConfirm__btn primary ${
              destructive ? "danger" : ""
            }`}
            disabled={copilotStore.streaming}
            onClick={() => copilotStore.respondConfirmation(block.id, true)}
          >
            {block.confirmLabel || "Confirm"}
          </button>
          <button
            type="button"
            className="copilotConfirm__btn ghost"
            disabled={copilotStore.streaming}
            onClick={() => copilotStore.respondConfirmation(block.id, false)}
          >
            {block.cancelLabel || "Cancel"}
          </button>
        </div>
      ) : (
        <div className="copilotConfirm__resolution">
          <i className="material-icons-outlined">
            {block.resolution === "confirmed" ? "check_circle" : "cancel"}
          </i>
          <span>
            {block.resolution === "confirmed"
              ? block.confirmLabel || "Confirmed"
              : block.cancelLabel || "Cancelled"}
          </span>
        </div>
      )}
    </div>
  );
});

export default ConfirmationBlock;
